// Visor de exit poll: avance de campo por punto y encuestador, y resultados por cortes horarios.
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Data, Layout } from "plotly.js-dist-min";
import {
  ExitPollAPI,
  ExitPollOptions,
  ExitPollPunto,
  ExitPollVariable,
} from "../api/endpoints";
import ProjectFormPicker from "../components/ProjectFormPicker";
import PlotlyChart, { COLORWAY } from "../components/Plot";
import { Card, ErrorBanner, HelpBox, InfoBanner, OkBanner, PageHeader, Spinner } from "../components/Layout";

const REFRESCO = [
  { v: 0, label: "Sin actualización automática" },
  { v: 60, label: "Cada 1 minuto" },
  { v: 120, label: "Cada 2 minutos" },
  { v: 300, label: "Cada 5 minutos" },
];

function fmtPct(v: number | undefined) {
  return v === undefined ? "—" : `${v.toFixed(1)} %`;
}

function Tile({ titulo, valor, nota }: { titulo: string; valor: string; nota?: string }) {
  return (
    <div className="tarjeta">
      <div className="etiqueta">{titulo}</div>
      <div className="text-2xl font-bold text-tinta">{valor}</div>
      {nota && <div className="text-xs text-tinta-3 mt-1">{nota}</div>}
    </div>
  );
}

function Barra({ pct }: { pct: number | null }) {
  const p = Math.max(0, Math.min(100, pct ?? 0));
  return (
    <div className="w-28 h-2 rounded bg-grid overflow-hidden" title={pct === null ? "Sin meta" : `${pct} %`}>
      <div className="h-2 bg-marca" style={{ width: `${p}%` }} />
    </div>
  );
}

function Alertas({ items }: { items: string[] }) {
  if (!items.length) return <span className="text-estado-ok text-xs">OK</span>;
  return (
    <div className="flex flex-col gap-0.5">
      {items.map((a) => (
        <span key={a} className="text-estado-critico text-xs">⚠ {a}</span>
      ))}
    </div>
  );
}

function TablaCortes({ v }: { v: ExitPollVariable }) {
  const cortes = v.cortes;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-grid text-left text-tinta-2">
            <th className="py-2 pr-3">Opción</th>
            {cortes.map((c) => (
              <th key={c.corte} className="py-2 px-2 text-right">
                {c.corte === "Total" ? "Total" : `Corte ${c.corte}`}
                {!c.disponible && <div className="text-xs text-tinta-3 font-normal">aún no llega</div>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {v.categorias.map((cat) => (
            <tr key={cat} className="border-b border-grid">
              <td className="py-1.5 pr-3 text-tinta">{cat}</td>
              {cortes.map((c) => (
                <td key={c.corte} className="py-1.5 px-2 text-right tabular-nums">{fmtPct(c.pct[cat])}</td>
              ))}
            </tr>
          ))}
          <tr className="text-tinta-3">
            <td className="py-1.5 pr-3">Entrevistas (n)</td>
            {cortes.map((c) => <td key={c.corte} className="py-1.5 px-2 text-right tabular-nums">{c.n}</td>)}
          </tr>
          <tr className="text-tinta-3">
            <td className="py-1.5 pr-3">Margen 95 % (máx., pts)</td>
            {cortes.map((c) => (
              <td key={c.corte} className="py-1.5 px-2 text-right tabular-nums">{c.margen === null ? "—" : `±${c.margen.toFixed(1)}`}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function GraficoHoras({ v, modo }: { v: ExitPollVariable; modo: "acumulado" | "franja" }) {
  const filas = modo === "acumulado" ? v.acumulado.map((a) => ({ x: `hasta ${a.hasta}`, ...a })) : v.por_hora.map((h) => ({ x: h.hora, ...h }));
  const data: Data[] = v.categorias.map((cat, i) => ({
    type: modo === "acumulado" ? "scatter" : "bar",
    mode: modo === "acumulado" ? "lines+markers" : undefined,
    name: cat,
    x: filas.map((f) => f.x),
    y: filas.map((f) => (f.n ? f.pct[cat] ?? 0 : null)),
    marker: { color: COLORWAY[i % COLORWAY.length] },
    hovertemplate: `${cat}: %{y:.1f} %<extra></extra>`,
  }) as Data);
  const layout: Partial<Layout> = {
    height: 340,
    barmode: "group",
    yaxis: { title: { text: "% sobre entrevistas con respuesta" }, rangemode: "tozero" },
    legend: { orientation: "h", y: -0.25 },
    margin: { t: 20, r: 20, l: 60, b: 90 },
  };
  return <PlotlyChart data={data} layout={layout} />;
}

export default function ExitPollPage() {
  const qc = useQueryClient();
  const [projectId, setProjectId] = useState<number | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [refresco, setRefresco] = useState<number>(120);
  const [variable, setVariable] = useState<string>("");
  const [modoGrafico, setModoGrafico] = useState<"acumulado" | "franja">("acumulado");
  const [aviso, setAviso] = useState<string>("");
  const [previa, setPrevia] = useState<{ puntos: ExitPollPunto[]; sin_coordenadas: string[]; encuestadores: number } | null>(null);
  const [errorCsv, setErrorCsv] = useState<string>("");
  const [borrador, setBorrador] = useState<Partial<ExitPollOptions> & { cortesTexto?: string; votosTexto?: string }>({});

  const listo = !!projectId && !!formId;
  const cfgQ = useQuery({
    queryKey: ["exitpoll-config", projectId, formId],
    queryFn: () => ExitPollAPI.config(projectId!, formId!),
    enabled: listo,
  });
  const resQ = useQuery({
    queryKey: ["exitpoll-resultados", projectId, formId],
    queryFn: () => ExitPollAPI.resultados(projectId!, formId!),
    enabled: listo,
    refetchInterval: refresco > 0 ? refresco * 1000 : false,
  });
  const leerAhora = useMutation({
    mutationFn: () => ExitPollAPI.resultados(projectId!, formId!, true),
    onSuccess: (data) => {
      qc.setQueryData(["exitpoll-resultados", projectId, formId], data);
      setAviso(`Leído de ODK a las ${data.ahora.slice(11)}.`);
    },
  });
  const guardar = useMutation({
    mutationFn: (body: { options?: Partial<ExitPollOptions>; puntos?: ExitPollPunto[] }) =>
      ExitPollAPI.guardar(projectId!, formId!, body),
    onSuccess: () => {
      setAviso("Configuración guardada.");
      setPrevia(null);
      setBorrador({});
      void qc.invalidateQueries({ queryKey: ["exitpoll-config", projectId, formId] });
      void qc.invalidateQueries({ queryKey: ["exitpoll-resultados", projectId, formId] });
    },
  });
  const leerCsv = useMutation({
    mutationFn: (texto: string) => ExitPollAPI.leerPuntos(texto),
    onSuccess: (r) => { setPrevia(r); setErrorCsv(""); },
    onError: (e: Error) => { setPrevia(null); setErrorCsv(e.message); },
  });

  const res = resQ.data;
  const cfg = cfgQ.data;
  const varActiva = useMemo(() => {
    if (!res?.variables.length) return null;
    return res.variables.find((v) => v.campo === variable) ?? res.variables[0];
  }, [res, variable]);

  const mapa = useMemo(() => {
    const pts = (res?.por_punto ?? []).filter((p) => p.lat !== null && p.lon !== null);
    if (!pts.length) return null;
    const lat = pts.reduce((s, p) => s + (p.lat ?? 0), 0) / pts.length;
    const lon = pts.reduce((s, p) => s + (p.lon ?? 0), 0) / pts.length;
    const corto = (n: string) => { const p = n.split(" "); return p.length >= 3 ? `${p[0]} ${p[p.length - 2]}` : n; };
    const data: Data[] = [{
      type: "scattermapbox",
      lat: pts.map((p) => p.lat),
      lon: pts.map((p) => p.lon),
      mode: "text+markers",
      textposition: "top right",
      textfont: { size: 12, color: "#0b0b0b" },
      text: pts.map((p, i) => `${i + 1}. ${(p.encuestadores_asignados.length ? p.encuestadores_asignados : p.encuestadores_vistos).map(corto).join(" + ") || p.nombre}`),
      marker: {
        size: pts.map((p) => 14 + Math.min(16, p.efectivas / 8)),
        color: pts.map((p) => (p.alertas.length ? 0 : Math.min(100, p.avance ?? 0))),
        colorscale: [[0, "#d03b3b"], [0.5, "#fab219"], [1, "#0ca30c"]],
        cmin: 0, cmax: 100, showscale: true, colorbar: { title: { text: "% avance" }, len: 0.6 },
      },
      hovertext: pts.map((p) => `<b>${p.nombre}</b><br>👤 ${(p.encuestadores_asignados.length ? p.encuestadores_asignados : p.encuestadores_vistos).join("<br>👤 ") || "—"}<br>${p.efectivas}${p.meta ? ` / ${p.meta}` : ""} efectivas · ${p.rechazos} rechazos<br>Última: ${p.ultima ?? "—"}${p.alertas.length ? `<br>⚠ ${p.alertas.join("<br>⚠ ")}` : ""}`),
      hoverinfo: "text",
    } as unknown as Data];
    // Teselas Esri: el servidor de OpenStreetMap rechaza (403) el uso desde navegadores sin política propia.
    const layout = {
      mapbox: {
        style: "white-bg", center: { lat, lon }, zoom: 12,
        layers: [{ sourcetype: "raster", below: "traces",
          source: ["https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"],
          sourceattribution: "Esri, HERE, Garmin, FAO, NOAA, USGS" }],
      },
      margin: { t: 0, b: 0, l: 0, r: 0 }, height: 480,
    } as unknown as Partial<Layout>;
    return { data, layout };
  }, [res]);

  const heat = useMemo(() => {
    const pts = res?.por_punto ?? [];
    if (!pts.length || !res?.resumen.efectivas) return null;   // sin datos el mapa de calor es un bloque sin sentido
    // Las claves "07".."09" no son enteros canónicos: JS las enumera DESPUÉS de "10".."17". Se ordenan.
    const horas = Object.keys(pts[0].por_hora).sort();
    const data: Data[] = [{
      type: "heatmap",
      x: horas.map((h) => `${h}:00`),
      y: pts.map((p) => p.nombre),
      z: pts.map((p) => horas.map((h) => p.por_hora[h] ?? 0)),
      colorscale: [[0, "#fcfcfb"], [1, "#2a78d6"]],
      hovertemplate: "%{y}<br>%{x}: %{z} efectivas<extra></extra>",
    } as Data];
    return { data, layout: { height: Math.max(260, 26 * pts.length + 80), margin: { t: 10, r: 10, l: 220, b: 40 } } as Partial<Layout> };
  }, [res]);

  const opts = cfg?.options;
  const valorBorrador = <K extends keyof ExitPollOptions>(k: K) =>
    (borrador[k] ?? opts?.[k]) as ExitPollOptions[K];

  const guardarOpciones = () => {
    const o: Partial<ExitPollOptions> = { ...borrador };
    delete (o as Record<string, unknown>).cortesTexto;
    delete (o as Record<string, unknown>).votosTexto;
    if (borrador.cortesTexto !== undefined) o.cortes = borrador.cortesTexto.split(",").map((s) => s.trim()).filter(Boolean);
    if (borrador.votosTexto !== undefined) o.campos_voto = borrador.votosTexto.split(",").map((s) => s.trim()).filter(Boolean);
    if (!Object.keys(o).length) { setAviso("No hay cambios que guardar."); return; }
    guardar.mutate({ options: o });
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Exit poll" subtitle="Avance de campo por punto y encuestador, y resultados por cortes horarios" />
      <HelpBox>
        <p><b>Qué muestra.</b> Lee en vivo las respuestas del formulario de boca de urna en ODK. Cada entrevista se
          ubica en un <b>punto</b> (local de votación de la muestra), con su <b>encuestador</b> y la <b>hora del contacto</b>.</p>
        <p><b>Cortes.</b> Cada corte es <b>acumulado</b>: todo lo que entró desde la apertura hasta esa hora (por defecto
          10:00, 13:00 y 15:00, más el total). El gráfico «Por franja» muestra en cambio solo lo que entró en cada hora.
          Nunca se promedian porcentajes de cortes acumulados.</p>
        <p><b>Ponderación.</b> Con «cada local pesa igual», cada local cuenta lo mismo sin importar cuántas entrevistas
          tenga: es el estimador correcto cuando los locales se sortearon en proporción a sus electores. El margen sale de
          la diferencia entre locales; con menos de 3 locales con datos se usa la aproximación de muestreo simple.</p>
        <p><b>Puntos.</b> Se cargan con un CSV (código, nombre, lat, lon, electores, meta y encuestador). Puede tener una
          fila por encuestador: se agrupan por código. Así el visor sabe la meta de cada punto y a quién se asignó.</p>
      </HelpBox>

      <Card>
        <ProjectFormPicker projectId={projectId} formId={formId}
          onProject={(id) => { setProjectId(id); setFormId(null); }} onForm={setFormId} />
      </Card>

      {listo && (
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="etiqueta">Actualización</label>
            <select className="input" value={refresco} onChange={(e) => setRefresco(Number(e.target.value))}>
              {REFRESCO.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
          </div>
          <button className="btn" onClick={() => leerAhora.mutate()} disabled={leerAhora.isPending}>
            {leerAhora.isPending ? <Spinner /> : "Leer ODK ahora"}
          </button>
          {res && (
            <span className="text-xs text-tinta-3">
              Hora del estudio {res.ahora.slice(11)} · ODK leído {new Date(res.leido_odk).toLocaleTimeString()} ·
              el servidor relee ODK como máximo cada {res.cache_segundos} s
            </span>
          )}
          {resQ.isFetching && !leerAhora.isPending && <Spinner />}
        </div>
      )}

      {aviso && <OkBanner message={aviso} />}
      {resQ.error && <ErrorBanner message={`No se pudieron calcular los resultados: ${(resQ.error as Error).message}`} />}
      {leerAhora.error && <ErrorBanner message={`No se pudo leer ODK: ${(leerAhora.error as Error).message}`} />}
      {cfgQ.error && <ErrorBanner message={`No se pudo leer la configuración: ${(cfgQ.error as Error).message}`} />}

      {listo && cfg?.puede_configurar && opts && (
        <details className="tarjeta" open={!cfg.puntos.length}>
          <summary className="cursor-pointer font-medium text-tinta">
            Configuración del visor · {cfg.puntos.length} puntos cargados
            {cfg.actualizado && <span className="text-xs text-tinta-3"> · guardada {new Date(cfg.actualizado).toLocaleString()}</span>}
          </summary>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {([
              ["campo_punto", "Campo del punto (local)"],
              ["campo_encuestador", "Campo del encuestador"],
              ["campo_hora", "Campo de la hora del contacto"],
              ["campo_aceptacion", "Campo aceptó / rechazó (vacío si no hay)"],
              ["valor_rechazo", "Código de «rechazó»"],
              ["campo_gps", "Campo GPS para controlar la ubicación"],
              ["campo_sexo", "Campo de sexo (observado)"],
              ["campo_edad", "Campo de rango de edad (observado)"],
              ["zona_horaria", "Zona horaria del estudio"],
            ] as [keyof ExitPollOptions, string][]).map(([k, label]) => (
              <div key={k}>
                <label className="etiqueta">{label}</label>
                <input className="input" value={String(valorBorrador(k) ?? "")}
                  onChange={(e) => setBorrador({ ...borrador, [k]: e.target.value })} />
              </div>
            ))}
            <div>
              <label className="etiqueta">Campos de voto (separados por coma)</label>
              <input className="input" value={borrador.votosTexto ?? opts.campos_voto.join(", ")}
                onChange={(e) => setBorrador({ ...borrador, votosTexto: e.target.value })} />
            </div>
            <div>
              <label className="etiqueta">Cortes acumulados (HH:MM, separados por coma)</label>
              <input className="input" value={borrador.cortesTexto ?? opts.cortes.join(", ")}
                onChange={(e) => setBorrador({ ...borrador, cortesTexto: e.target.value })} />
            </div>
            <div>
              <label className="etiqueta">Ponderación</label>
              <select className="input" value={valorBorrador("ponderacion")}
                onChange={(e) => setBorrador({ ...borrador, ponderacion: e.target.value as ExitPollOptions["ponderacion"] })}>
                <option value="local">Cada local pesa igual (muestra proporcional a electores)</option>
                <option value="electores">Cada local pesa según sus electores</option>
                <option value="ninguna">Sin ponderar (proporción de entrevistas)</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="etiqueta">Hora de apertura</label>
                <input className="input" type="number" min={0} max={23} value={valorBorrador("hora_apertura")}
                  onChange={(e) => setBorrador({ ...borrador, hora_apertura: Number(e.target.value) })} />
              </div>
              <div>
                <label className="etiqueta">Hora de cierre</label>
                <input className="input" type="number" min={1} max={24} value={valorBorrador("hora_cierre")}
                  onChange={(e) => setBorrador({ ...borrador, hora_cierre: Number(e.target.value) })} />
              </div>
            </div>
          </div>
          <div className="mt-3">
            <button className="btn" onClick={guardarOpciones} disabled={guardar.isPending}>
              {guardar.isPending ? <Spinner /> : "Guardar configuración"}
            </button>
          </div>
          {guardar.error && <div className="mt-2"><ErrorBanner message={(guardar.error as Error).message} /></div>}

          <div className="mt-6 border-t border-grid pt-4">
            <label className="etiqueta">Cargar puntos de la muestra (CSV)</label>
            <input type="file" accept=".csv,text/csv" className="input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const r = new FileReader();
                r.onload = () => leerCsv.mutate(String(r.result ?? ""));
                r.onerror = () => setErrorCsv("No se pudo leer el archivo.");
                r.readAsText(f, "utf-8");
              }} />
            {leerCsv.isPending && <div className="mt-2"><Spinner /></div>}
            {errorCsv && <div className="mt-2"><ErrorBanner message={errorCsv} /></div>}
            {previa && (
              <div className="mt-3 space-y-2">
                <InfoBanner message={`Se leyeron ${previa.puntos.length} puntos y ${previa.encuestadores} encuestadores asignados.` +
                  (previa.sin_coordenadas.length ? ` Sin coordenadas: ${previa.sin_coordenadas.join(", ")} (no saldrán en el mapa).` : "") +
                  " Revise y guarde: esto REEMPLAZA los puntos cargados."} />
                <div className="max-h-60 overflow-auto text-xs">
                  <table className="w-full">
                    <thead><tr className="text-left text-tinta-2 border-b border-grid">
                      <th className="py-1">Código</th><th>Nombre</th><th>Lat</th><th>Lon</th><th>Electores</th><th>Meta</th><th>Encuestadores</th>
                    </tr></thead>
                    <tbody>
                      {previa.puntos.map((p) => (
                        <tr key={p.codigo} className="border-b border-grid">
                          <td className="py-1">{p.codigo}</td><td>{p.nombre}</td><td>{p.lat ?? "—"}</td><td>{p.lon ?? "—"}</td>
                          <td>{p.electores ?? "—"}</td><td>{p.meta ?? "—"}</td><td>{p.encuestadores.join(", ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-2">
                  <button className="btn" onClick={() => guardar.mutate({ puntos: previa.puntos })} disabled={guardar.isPending}>Guardar puntos</button>
                  <button className="btn-sec" onClick={() => setPrevia(null)}>Descartar</button>
                </div>
              </div>
            )}
          </div>
        </details>
      )}

      {listo && resQ.isLoading && <Card><Spinner /> Leyendo ODK…</Card>}

      {res && (
        <>
          {res.resumen.puntos_total === 0 && (
            <InfoBanner message="No hay puntos cargados: el avance por punto y por encuestador no tiene contra qué compararse. Cargue el CSV de la muestra en «Configuración del visor»." />
          )}
          {Object.keys(res.resumen.fuera_de_muestra).length > 0 && (
            <InfoBanner message={`Hay entrevistas en puntos que no están en la muestra: ${Object.entries(res.resumen.fuera_de_muestra).map(([k, n]) => `${k} (${n})`).join(", ")}.`} />
          )}
          {res.resumen.sin_hora > 0 && (
            <InfoBanner message={`${res.resumen.sin_hora} entrevista(s) sin hora reconocible: cuentan en el total pero no en los cortes.`} />
          )}

          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Tile titulo="Efectivas" valor={String(res.resumen.efectivas)} nota={res.resumen.meta_total ? `meta ${res.resumen.meta_total}` : undefined} />
            <Tile titulo="Avance" valor={res.resumen.avance === null ? "—" : `${res.resumen.avance} %`} />
            <Tile titulo="Rechazos" valor={String(res.resumen.rechazos)} />
            <Tile titulo="Aceptación" valor={res.resumen.aceptacion === null ? "—" : `${res.resumen.aceptacion} %`} />
            <Tile titulo="Puntos con datos" valor={`${res.resumen.puntos_con_datos} / ${res.resumen.puntos_total}`} />
            <Tile titulo="Encuestadores activos" valor={String(res.resumen.encuestadores_activos)} />
          </div>

          {varActiva && (
            <Card>
              <div className="flex flex-wrap items-end gap-3 mb-3">
                <div>
                  <label className="etiqueta">Variable</label>
                  <select className="input" value={varActiva.campo} onChange={(e) => setVariable(e.target.value)}>
                    {res.variables.map((v) => <option key={v.campo} value={v.campo}>{v.campo}</option>)}
                  </select>
                </div>
                <div className="text-xs text-tinta-3">
                  Ponderación: {res.variables.length ? (cfg?.options.ponderacion === "local" ? "cada local pesa igual" : cfg?.options.ponderacion === "electores" ? "por electores" : "sin ponderar") : ""}
                  {varActiva.sin_respuesta > 0 && ` · ${varActiva.sin_respuesta} efectivas sin respuesta en esta variable`}
                </div>
              </div>
              <h3 className="font-semibold text-tinta mb-2">Cortes acumulados</h3>
              <TablaCortes v={varActiva} />
              <div className="flex items-center gap-2 mt-6 mb-2">
                <h3 className="font-semibold text-tinta">Por hora</h3>
                <select className="input w-auto" value={modoGrafico} onChange={(e) => setModoGrafico(e.target.value as "acumulado" | "franja")}>
                  <option value="acumulado">Acumulado hasta cada hora</option>
                  <option value="franja">Solo lo de cada franja</option>
                </select>
              </div>
              <GraficoHoras v={varActiva} modo={modoGrafico} />
            </Card>
          )}

          {res.perfil && (
            <Card>
              <h3 className="font-semibold text-tinta mb-1">Perfil observado (sexo y edad)</h3>
              <p className="text-xs text-tinta-3 mb-3">Percepción del encuestador, registrada también en los rechazos: si la aceptación es muy distinta entre grupos, el resultado necesita ajuste por no respuesta.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(["sexo", "edad"] as const).map((k) => (
                  <div key={k}>
                    <div className="etiqueta">{k === "sexo" ? "Sexo" : "Rango de edad"}</div>
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-grid text-left text-tinta-2">
                        <th className="py-1.5">Grupo</th><th className="text-right">Efectivas</th><th className="text-right">% de efectivas</th>
                        <th className="text-right">Rechazos</th><th className="text-right">Aceptación</th>
                      </tr></thead>
                      <tbody>
                        {res.perfil[k].map((p) => (
                          <tr key={p.categoria} className="border-b border-grid">
                            <td className="py-1.5 text-tinta">{p.categoria}</td>
                            <td className="text-right tabular-nums">{p.efectivas}</td>
                            <td className="text-right tabular-nums">{p.pct_efectivas === null ? "—" : `${p.pct_efectivas} %`}</td>
                            <td className="text-right tabular-nums">{p.rechazos}</td>
                            <td className="text-right tabular-nums">{p.aceptacion === null ? "—" : `${p.aceptacion} %`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
              {varActiva && varActiva.por_sexo.length > 0 && (
                <div className="mt-6 overflow-x-auto">
                  <div className="etiqueta">Voto por sexo · {varActiva.campo}</div>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-grid text-left text-tinta-2">
                      <th className="py-1.5">Opción</th>
                      {varActiva.por_sexo.map((x) => <th key={x.sexo} className="text-right px-2">{x.sexo} (n={x.n})</th>)}
                    </tr></thead>
                    <tbody>
                      {varActiva.categorias.map((cat) => (
                        <tr key={cat} className="border-b border-grid">
                          <td className="py-1.5 text-tinta">{cat}</td>
                          {varActiva.por_sexo.map((x) => <td key={x.sexo} className="text-right px-2 tabular-nums">{fmtPct(x.pct[cat])}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          <Card>
            <h3 className="font-semibold text-tinta mb-3">Avance por punto</h3>
            {mapa && <div className="mb-4"><PlotlyChart data={mapa.data} layout={mapa.layout} /></div>}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-grid text-left text-tinta-2">
                    <th className="py-2 pr-2">Punto</th><th className="px-2">Encuestadores</th><th className="px-2 text-right">Efectivas</th>
                    <th className="px-2">Avance</th><th className="px-2 text-right">Rechazos</th><th className="px-2 text-right">Acept.</th>
                    <th className="px-2">Última</th><th className="px-2">Alertas</th>
                  </tr>
                </thead>
                <tbody>
                  {res.por_punto.map((p) => (
                    <tr key={p.codigo} className="border-b border-grid align-top">
                      <td className="py-1.5 pr-2 text-tinta">{p.nombre}<div className="text-xs text-tinta-3">{p.codigo}{p.sector ? ` · ${p.sector}` : ""}</div></td>
                      <td className="px-2 text-xs text-tinta-2">{(p.encuestadores_asignados.length ? p.encuestadores_asignados : p.encuestadores_vistos).join(", ") || "—"}</td>
                      <td className="px-2 text-right tabular-nums">{p.efectivas}{p.meta ? <span className="text-tinta-3"> / {p.meta}</span> : null}</td>
                      <td className="px-2"><Barra pct={p.avance} /><div className="text-xs text-tinta-3">{p.avance === null ? "sin meta" : `${p.avance} %`}</div></td>
                      <td className="px-2 text-right tabular-nums">{p.rechazos}</td>
                      <td className="px-2 text-right tabular-nums">{p.aceptacion === null ? "—" : `${p.aceptacion} %`}</td>
                      <td className="px-2 tabular-nums">{p.ultima ?? "—"}{p.minutos_sin_envio !== null && <div className="text-xs text-tinta-3">hace {p.minutos_sin_envio} min</div>}</td>
                      <td className="px-2"><Alertas items={p.alertas} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {heat && (
              <>
                <h4 className="font-medium text-tinta mt-6 mb-2">Efectivas por punto y hora</h4>
                <PlotlyChart data={heat.data} layout={heat.layout} />
              </>
            )}
          </Card>

          <Card>
            <h3 className="font-semibold text-tinta mb-3">Avance por encuestador</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-grid text-left text-tinta-2">
                    <th className="py-2 pr-2">Encuestador</th><th className="px-2">Punto asignado</th><th className="px-2 text-right">Efectivas</th>
                    <th className="px-2 text-right">Rechazos</th><th className="px-2 text-right">Ritmo/h</th><th className="px-2">Primera / última</th>
                    <th className="px-2 text-right">GPS al punto</th><th className="px-2">Alertas</th>
                  </tr>
                </thead>
                <tbody>
                  {res.por_encuestador.map((e) => (
                    <tr key={e.encuestador} className="border-b border-grid align-top">
                      <td className="py-1.5 pr-2 text-tinta">{e.encuestador}</td>
                      <td className="px-2 text-xs text-tinta-2">{e.punto_asignado_nombre ?? e.punto_asignado ?? "—"}</td>
                      <td className="px-2 text-right tabular-nums">{e.efectivas}</td>
                      <td className="px-2 text-right tabular-nums">{e.rechazos}</td>
                      <td className="px-2 text-right tabular-nums">{e.ritmo_hora ?? "—"}</td>
                      <td className="px-2 tabular-nums">{e.primera ?? "—"} – {e.ultima ?? "—"}</td>
                      <td className="px-2 text-right tabular-nums">{e.distancia_mediana_m === null ? "—" : `${e.distancia_mediana_m} m`}</td>
                      <td className="px-2"><Alertas items={e.alertas} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
